/* LIORA FASHION — "Édition" interactions
   Ajax cart, variant picker, mega menu + GSAP ScrollTrigger choreography.
   Every animation is optional: without GSAP (or with reduced motion) the page stays fully readable. */

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
const i18n = window.__i18n || {};
const routes = Object.assign({ cart: "/cart", cartAdd: "/cart/add", cartChange: "/cart/change" }, window.__routes || {});
const root = document.documentElement;
window.__lioraReady = true;

/* mirrors Shopify's {{ amount }} placeholders so JS prices match Liquid's | money */
function money(cents) {
  const format = window.__moneyFormat || "CHF {{amount}}";
  const group = (decimals, thousands, decimal) => {
    const parts = (cents / 100).toFixed(decimals).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case "amount_no_decimals": return group(0, ",", ".");
      case "amount_with_comma_separator": return group(2, ".", ",");
      case "amount_no_decimals_with_comma_separator": return group(0, ".", ",");
      case "amount_with_apostrophe_separator": return group(2, "'", ".");
      case "amount_with_space_separator": return group(2, " ", ",");
      default: return group(2, ",", ".");
    }
  }).replace(/<[^>]*>/g, "");
}
function esc(str) {
  return String(str == null ? "" : str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function sized(src, width) {
  if (!src) return "";
  try { const u = new URL(src, window.location.origin); u.searchParams.set("width", width); return u.toString(); }
  catch (e) { return src; }
}
function onScrollFrame(fn) {
  let ticking = false;
  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { fn(); ticking = false; });
  }, { passive: true });
  fn();
}

/* ======================================================================
   Cart
   ====================================================================== */
let lastFocus = null;
function openCart() {
  const drawer = document.getElementById("cart-drawer");
  if (!drawer) return;
  lastFocus = document.activeElement;
  document.getElementById("cart-overlay")?.classList.add("open");
  drawer.classList.add("open");
  document.body.classList.add("is-locked");
  setTimeout(() => drawer.focus({ preventScroll: true }), 80);
}
function closeCart() {
  const drawer = document.getElementById("cart-drawer");
  if (!drawer || !drawer.classList.contains("open")) return;
  document.getElementById("cart-overlay")?.classList.remove("open");
  drawer.classList.remove("open");
  if (!document.body.classList.contains("menu-open")) document.body.classList.remove("is-locked");
  lastFocus?.focus?.({ preventScroll: true });
}
function setCount(count) {
  document.querySelectorAll("[data-cart-count]").forEach((el) => {
    const changed = el.textContent.trim() !== String(count);
    el.textContent = count;
    if (changed && el.classList.contains("cart-link__count")) {
      el.classList.add("bump");
      setTimeout(() => el.classList.remove("bump"), 600);
    }
  });
}
function lineHtml(item, index) {
  const line = index + 1;
  const variant = item.variant_title && !item.product_has_only_default_variant ? `<div class="line__meta">${esc(item.variant_title)}</div>` : "";
  return `
    <div class="line">
      <a href="${esc(item.url)}" class="line__img">${item.image ? `<img src="${esc(sized(item.image, 240))}" alt="">` : ""}</a>
      <div>
        <a href="${esc(item.url)}" class="line__title">${esc(item.product_title)}</a>
        ${variant}
        <div class="line__row">
          <div class="qty">
            <button type="button" data-line="${line}" data-qty="${item.quantity - 1}" aria-label="${esc(i18n.decrease)}">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-line="${line}" data-qty="${item.quantity + 1}" aria-label="${esc(i18n.increase)}">+</button>
          </div>
          <button type="button" class="line__rm" data-line="${line}" data-qty="0">${esc(i18n.entfernen)}</button>
        </div>
      </div>
      <div class="line__price">${money(item.final_line_price)}</div>
    </div>`;
}
function renderCart(cart) {
  const items = document.getElementById("cart-items");
  if (!items) return;
  const foot = document.getElementById("cart-foot");
  if (cart.item_count === 0) {
    items.innerHTML = document.getElementById("cart-empty-tpl")?.innerHTML || "";
    if (foot) foot.hidden = true;
  } else {
    items.innerHTML = cart.items.map(lineHtml).join("");
    if (foot) foot.hidden = false;
    if (window.gsap && !reduceMotion) gsap.from(items.querySelectorAll(".line"), { x: 40, opacity: 0, duration: .6, stagger: .06, ease: "expo.out" });
  }
  const sub = document.getElementById("cart-subtotal");
  if (sub) sub.textContent = money(cart.total_price);
  renderShip(cart.total_price);
  setCount(cart.item_count);
}
/* free-shipping progress: threshold comes from the theme setting (window.__shipFreeFrom, in cents) */
function renderShip(total) {
  const free = window.__shipFreeFrom || 0;
  if (!free) return;
  const remaining = free - total;
  document.querySelectorAll("[data-ship]").forEach((el) => {
    el.classList.toggle("is-free", remaining <= 0);
    const txt = el.querySelector("[data-ship-text] span");
    if (txt) txt.textContent = remaining > 0 ? (i18n.shipRemaining || "").replace("__AMOUNT__", money(remaining)) : i18n.shipFree;
    const fill = el.querySelector("[data-ship-fill]");
    if (fill) fill.style.width = `${Math.min(100, Math.max(0, total / free * 100))}%`;
  });
}
async function fetchCart() {
  const res = await fetch(routes.cart + ".js", { headers: { Accept: "application/json" } });
  return res.json();
}
async function addToCart(id, quantity = 1) {
  const res = await fetch(routes.cartAdd + ".js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ id, quantity })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    window.alert(err.description || err.message || "Error");
    return;
  }
  renderCart(await fetchCart());
  openCart();
}
async function changeLine(line, quantity) {
  const drawer = document.getElementById("cart-drawer");
  drawer?.classList.add("is-busy");
  try {
    await fetch(routes.cartChange + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line, quantity: Math.max(0, quantity) })
    });
    renderCart(await fetchCart());
  } finally {
    drawer?.classList.remove("is-busy");
  }
}
function initCart() {
  const onCartPage = document.body.classList.contains("tpl-cart");
  document.getElementById("cart-toggle")?.addEventListener("click", (e) => {
    if (onCartPage) return;
    e.preventDefault();
    openCart();
  });
  document.getElementById("cart-close")?.addEventListener("click", closeCart);
  document.getElementById("cart-overlay")?.addEventListener("click", closeCart);
  document.getElementById("cart-items")?.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-line]");
    if (btn) changeLine(parseInt(btn.dataset.line, 10), parseInt(btn.dataset.qty, 10));
  });
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-quick-add]");
    if (!btn) return;
    e.preventDefault();
    btn.classList.add("is-loading");
    try { await addToCart(parseInt(btn.dataset.quickAdd, 10)); } finally { btn.classList.remove("is-loading"); }
  });
  document.querySelectorAll("[data-qty] [data-step]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const input = btn.parentElement.querySelector("input");
      if (input) input.value = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.step, 10));
    });
  });
  fetchCart().then(renderCart).catch(() => {});
}

/* ======================================================================
   Header: sticky state, mega menu, mobile menu
   ====================================================================== */
function closeMenu() {
  if (!document.body.classList.contains("menu-open")) return;
  document.body.classList.remove("menu-open", "is-locked");
  document.getElementById("nav-toggle")?.setAttribute("aria-expanded", "false");
}
function initHeader() {
  const hdr = document.getElementById("hdr");
  if (!hdr) return;

  let lastY = window.scrollY;
  onScrollFrame(() => {
    const y = window.scrollY;
    const busy = document.body.classList.contains("menu-open") || hdr.querySelector(".nav-item.is-open");
    hdr.classList.toggle("is-scrolled", y > 10);
    if (!busy) {
      if (y > 420 && y > lastY + 6) hdr.classList.add("is-hidden");
      else if (y < lastY - 6 || y < 420) hdr.classList.remove("is-hidden");
    }
    lastY = y;
  });

  /* mega menu */
  const items = Array.from(hdr.querySelectorAll(".nav-item[data-mega]"));
  const setOpen = (item, open) => {
    item.classList.toggle("is-open", open);
    item.querySelector(".nav-link")?.setAttribute("aria-expanded", open ? "true" : "false");
  };
  items.forEach((item) => {
    let timer;
    const link = item.querySelector(".nav-link");
    item.addEventListener("pointerenter", (e) => {
      if (e.pointerType !== "mouse") return;
      clearTimeout(timer);
      timer = setTimeout(() => { items.forEach((o) => setOpen(o, o === item)); }, 90);
    });
    item.addEventListener("pointerleave", (e) => {
      if (e.pointerType !== "mouse") return;
      clearTimeout(timer);
      timer = setTimeout(() => setOpen(item, false), 160);
    });
    item.addEventListener("focusin", () => items.forEach((o) => setOpen(o, o === item)));
    item.addEventListener("focusout", (e) => { if (!item.contains(e.relatedTarget)) setOpen(item, false); });
    /* touch on a wide screen: first tap opens, second follows the link */
    link?.addEventListener("click", (e) => {
      if (!finePointer && !item.classList.contains("is-open")) {
        e.preventDefault();
        items.forEach((o) => setOpen(o, o === item));
      }
    });
  });

  /* mobile menu */
  const toggle = document.getElementById("nav-toggle");
  toggle?.addEventListener("click", () => {
    const open = !document.body.classList.contains("menu-open");
    if (open) root.style.setProperty("--mnav-top", Math.max(0, hdr.getBoundingClientRect().bottom) + "px");
    document.body.classList.toggle("menu-open", open);
    document.body.classList.toggle("is-locked", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    hdr.classList.remove("is-hidden");
  });
  document.querySelectorAll(".mnav__toggle").forEach((btn) => {
    btn.addEventListener("click", () => {
      const group = btn.closest(".mnav__group");
      const open = !group.classList.contains("is-open");
      group.classList.toggle("is-open", open);
      btn.setAttribute("aria-expanded", open ? "true" : "false");
    });
  });
  window.matchMedia("(min-width: 1361px)").addEventListener("change", (e) => { if (e.matches) closeMenu(); });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    closeCart();
    closeMenu();
    items.forEach((o) => setOpen(o, false));
  });
}

/* ======================================================================
   Collection grid toggle
   ====================================================================== */
function initGridToggle() {
  const grid = document.getElementById("product-grid");
  const buttons = document.querySelectorAll(".ctabs__view [data-view]");
  if (!grid || !buttons.length) return;
  const apply = (cols) => {
    grid.dataset.cols = cols;
    grid.style.setProperty("--cols", cols);
    if (cols === "4") grid.style.removeProperty("--cols");
    buttons.forEach((b) => b.classList.toggle("is-active", b.dataset.view === cols));
  };
  let saved = null;
  try { saved = localStorage.getItem("liora-grid"); } catch (e) { saved = null; }
  if (saved === "2") apply("2");
  buttons.forEach((b) => b.addEventListener("click", () => {
    apply(b.dataset.view);
    try { localStorage.setItem("liora-grid", b.dataset.view); } catch (e) { /* storage blocked */ }
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  }));
}

/* ======================================================================
   Product page
   ====================================================================== */
function initProduct() {
  const form = document.getElementById("product-form");
  const product = window.__productJSON;
  if (!form || !product) return;
  const idInput = document.getElementById("variant-id");
  const addBtn = document.getElementById("pdp-add");
  const barBtn = document.getElementById("pdp-bar-add");
  const priceEl = document.getElementById("pdp-price");
  const compareEl = document.getElementById("pdp-compare");
  const barPrice = document.querySelector("[data-bar-price]");
  const groups = Array.from(form.querySelectorAll(".opt__values"));
  const selected = groups.map((g) => (g.querySelector(".is-active") || g.querySelector(".opt__btn"))?.dataset.value);

  const current = () => {
    if (!groups.length) return product.variants[0];
    return product.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
  };
  const setLabel = (btn, available) => {
    if (!btn) return;
    btn.disabled = !available;
    const span = btn.querySelector("span");
    if (span) span.textContent = available ? i18n.addToCart : i18n.soldOut;
  };
  const markUnavailable = () => {
    groups.forEach((g, gi) => {
      g.querySelectorAll(".opt__btn").forEach((b) => {
        const probe = selected.slice();
        probe[gi] = b.dataset.value;
        const v = product.variants.find((x) => x.options.every((o, i) => o === probe[i]));
        b.classList.toggle("is-unavailable", !v || !v.available);
      });
    });
  };
  const update = (initial) => {
    const v = current();
    if (!v) { setLabel(addBtn, false); setLabel(barBtn, false); return; }
    if (idInput) idInput.value = v.id;
    if (priceEl) priceEl.textContent = money(v.price);
    if (barPrice) barPrice.textContent = money(v.price);
    if (compareEl) compareEl.textContent = v.compare_at_price > v.price ? money(v.compare_at_price) : "";
    setLabel(addBtn, v.available);
    setLabel(barBtn, v.available);
    markUnavailable();
    if (v.featured_media && !initial) {
      const fig = document.querySelector(`.pdp__img[data-media-id="${v.featured_media.id}"]`);
      const gallery = document.getElementById("pdp-gallery");
      if (fig && gallery) {
        if (gallery.scrollWidth > gallery.clientWidth) {
          gallery.scrollTo({ left: fig.offsetLeft, behavior: reduceMotion ? "auto" : "smooth" });
        } else if (fig !== gallery.firstElementChild) {
          /* desktop stack: bring the variant's image to the hero slot instead of jumping the page */
          gallery.prepend(fig);
          if (window.gsap && !reduceMotion) gsap.fromTo(fig, { clipPath: "inset(0% 0% 100% 0%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: .9, ease: "expo.inOut" });
        }
      }
    }
    if (!initial) {
      /* bring the image back into view so the new colour is visible */
      const gallery = document.getElementById("pdp-gallery");
      const hdrH = document.getElementById("hdr")?.offsetHeight || 0;
      if (gallery && gallery.getBoundingClientRect().top < hdrH - 1) {
        window.scrollTo({ top: Math.max(0, gallery.getBoundingClientRect().top + window.scrollY - hdrH - 12), behavior: reduceMotion ? "auto" : "smooth" });
      }
    }
  };
  groups.forEach((g, gi) => {
    g.addEventListener("click", (e) => {
      const btn = e.target.closest(".opt__btn");
      if (!btn) return;
      selected[gi] = btn.dataset.value;
      g.querySelectorAll(".opt__btn").forEach((b) => {
        b.classList.toggle("is-active", b === btn);
        b.setAttribute("aria-pressed", b === btn ? "true" : "false");
      });
      const label = g.parentElement.querySelector("[data-opt-label]");
      if (label) label.textContent = btn.textContent.trim(); // translated display text, not the raw option value
      update();
    });
  });
  const submit = async () => {
    const v = current();
    if (!v || !v.available) return;
    addBtn?.classList.add("is-loading");
    try { await addToCart(v.id); } finally { addBtn?.classList.remove("is-loading"); }
  };
  form.addEventListener("submit", (e) => { e.preventDefault(); submit(); });
  barBtn?.addEventListener("click", submit);
  update(true);

  /* mobile gallery counter */
  const gallery = document.getElementById("pdp-gallery");
  const count = document.getElementById("pdp-count");
  if (gallery && count) {
    const total = gallery.children.length;
    gallery.addEventListener("scroll", () => {
      const i = Math.round(gallery.scrollLeft / gallery.clientWidth) + 1;
      count.textContent = `${String(i).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
    }, { passive: true });
  }

  /* sticky buy bar once the main button is out of view */
  const bar = document.getElementById("pdp-bar");
  if (bar && addBtn) {
    onScrollFrame(() => {
      const show = addBtn.getBoundingClientRect().bottom < 0;
      bar.classList.toggle("show", show);
      bar.setAttribute("aria-hidden", show ? "false" : "true");
      if (barBtn) barBtn.tabIndex = show ? 0 : -1;
    });
  }
}

/* ======================================================================
   Text splitting (keeps child elements like <sup> intact)
   ====================================================================== */
function splitWords(el) {
  if (el.dataset.splitDone) return el.querySelectorAll(".split-w > span");
  const label = el.textContent.replace(/\s+/g, " ").trim();
  const frag = document.createDocumentFragment();
  Array.from(el.childNodes).forEach((node) => {
    if (node.nodeType === 3) {
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(" ")); return; }
        const w = document.createElement("span");
        w.className = "split-w";
        w.innerHTML = `<span>${esc(part)}</span>`;
        frag.appendChild(w);
      });
    } else {
      const w = document.createElement("span");
      w.className = "split-w";
      const inner = document.createElement("span");
      inner.appendChild(node.cloneNode(true));
      w.appendChild(inner);
      frag.appendChild(w);
    }
  });
  el.textContent = "";
  el.appendChild(frag);
  el.setAttribute("aria-label", label);
  el.querySelectorAll(".split-w").forEach((w) => w.setAttribute("aria-hidden", "true"));
  el.dataset.splitDone = "1";
  return el.querySelectorAll(".split-w > span");
}
function splitChars(el) {
  const text = el.textContent;
  el.setAttribute("aria-label", text.trim());
  el.innerHTML = text.split(/(\s+)/).map((word) => {
    if (/^\s+$/.test(word)) return " ";
    return `<span style="display:inline-block;white-space:nowrap" aria-hidden="true">${Array.from(word).map((c) => `<span class="ch">${esc(c)}</span>`).join("")}</span>`;
  }).join("");
  return el.querySelectorAll(".ch");
}

/* ======================================================================
   Motion (GSAP)
   ====================================================================== */
function initMotion() {
  const gs = window.gsap;
  const ST = window.ScrollTrigger;
  if (!gs || !ST || reduceMotion) {
    root.classList.remove("gsap-on");
    root.classList.add("no-gsap");
    document.querySelectorAll("[data-chars]").forEach(splitChars);
    document.querySelectorAll(".mani__text .ch").forEach((c) => c.classList.add("is-on"));
    document.getElementById("intro")?.remove();
    return;
  }
  gs.registerPlugin(ST);
  root.classList.add("gsap-on");
  const mm = gs.matchMedia();

  /* --- intro loader (homepage, once per session) --- */
  const intro = document.getElementById("intro");
  let heroDelay = 0.1;
  if (intro && !root.classList.contains("intro-seen")) {
    heroDelay = 1.55;
    const counter = { v: 0 };
    const countEl = document.getElementById("intro-count");
    gs.timeline({ onComplete: () => intro.remove() })
      .fromTo(intro.querySelectorAll(".intro__word span"), { y: 0, yPercent: 105 }, { yPercent: 0, duration: .9, stagger: .07, ease: "expo.out" }, 0)
      .to(counter, { v: 100, duration: 1.2, ease: "power2.inOut", onUpdate: () => { if (countEl) countEl.textContent = String(Math.round(counter.v)).padStart(3, "0"); } }, 0)
      .to(intro, { yPercent: -100, duration: .8, ease: "expo.inOut" }, 1.15);
    try { sessionStorage.setItem("liora-intro", "1"); } catch (e) { /* ignore */ }
  } else {
    intro?.remove();
  }

  /* --- homepage cover --- */
  const cover = document.getElementById("cover");
  if (cover) {
    const media = cover.querySelector("[data-hero-media]");
    const title = cover.querySelector(".hero__title");
    const words = title ? splitWords(title) : [];
    const tl = gs.timeline({ delay: heroDelay, defaults: { ease: "expo.out" } });
    if (media) {
      tl.fromTo(media, { clipPath: "inset(100% 0% 0% 0%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: 1.4, ease: "expo.inOut" }, 0)
        .from(media.querySelector(".runway__stage") || media.querySelector("img"), { scale: 1.25, duration: 2 }, 0);
    }
    tl.from(words, { yPercent: 110, duration: 1.1, stagger: .05 }, .6)
      .fromTo(cover.querySelectorAll(".hero__panel [data-reveal]"), { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, stagger: .1 }, .9);
    const stage = cover.querySelector(".runway__stage");
    if (stage) {
      /* depth: the runway drifts against the pointer and slides away on scroll */
      gs.to(stage, { yPercent: -8, ease: "none", scrollTrigger: { trigger: cover, start: "top top", end: "bottom top", scrub: true } });
      if (finePointer) {
        const sx = gs.quickTo(stage, "x", { duration: 1.2, ease: "power3" });
        const sy = gs.quickTo(stage, "y", { duration: 1.2, ease: "power3" });
        cover.addEventListener("pointermove", (e) => {
          const r = cover.getBoundingClientRect();
          sx(((e.clientX - r.left) / r.width - .5) * -40);
          sy(((e.clientY - r.top) / r.height - .5) * -30);
        });
      }
      tl.from(cover.querySelectorAll(".runway__col"), { yPercent: 30, opacity: 0, duration: 1.6, stagger: .12, ease: "expo.out" }, .2)
        .from(cover.querySelectorAll(".runway__chip"), { scale: .6, opacity: 0, duration: .9, stagger: .12, ease: "back.out(1.6)" }, 1.1);
    }
  }

  /* --- split headings --- */
  document.querySelectorAll("[data-split]").forEach((el) => {
    if (el.closest("#cover")) return;
    const words = splitWords(el);
    gs.from(words, { yPercent: 110, duration: 1.1, stagger: .05, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 88%", once: true } });
  });

  /* --- generic reveals, batched --- */
  ST.batch("[data-reveal]:not(.hero__panel [data-reveal])", {
    start: "top 90%",
    once: true,
    onEnter: (batch) => gs.fromTo(batch, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1, stagger: .08, ease: "expo.out", overwrite: true })
  });

  /* --- image curtains --- */
  document.querySelectorAll("[data-curtain]").forEach((el) => {
    const img = el.querySelector("img");
    const tl = gs.timeline({ scrollTrigger: { trigger: el, start: "top 85%", once: true }, delay: el.closest("#cover") ? heroDelay + .4 : 0 });
    tl.fromTo(el, { clipPath: "inset(100% 0% 0% 0%)" }, { clipPath: "inset(0% 0% 0% 0%)", duration: 1.3, ease: "expo.inOut" });
    if (img) tl.from(img, { scale: 1.3, duration: 1.6, ease: "expo.out" }, 0);
  });

  /* --- parallax images --- */
  document.querySelectorAll("[data-parallax]").forEach((img) => {
    gs.fromTo(img, { yPercent: -7 }, { yPercent: 7, ease: "none", scrollTrigger: { trigger: img.parentElement, start: "top bottom", end: "bottom top", scrub: true } });
  });

  /* --- velocity marquee --- */
  const vm = document.querySelector("[data-vmarq] .vmarq__track");
  if (vm) {
    let x = 0, skew = 0, velocity = 0;
    const half = () => vm.scrollWidth / 2;
    ST.create({ trigger: vm, start: "top bottom", end: "bottom top", onUpdate: (self) => { velocity = self.getVelocity(); } });
    gs.ticker.add(() => {
      const v = gs.utils.clamp(-3000, 3000, velocity);
      x -= 1.1 + Math.abs(v) / 260;
      const h = half();
      if (h > 0 && -x >= h) x += h;
      skew += ((v / -220) - skew) * .1;
      velocity *= .92;
      gs.set(vm, { x, skewX: gs.utils.clamp(-12, 12, skew) });
    });
  }

  /* --- category index with cursor-following preview --- */
  const preview = document.getElementById("index-preview");
  const rows = document.querySelectorAll("[data-index] .index__row");
  if (preview && rows.length && finePointer) {
    const imgs = preview.querySelectorAll("img");
    const xTo = gs.quickTo(preview, "x", { duration: .6, ease: "power3" });
    const yTo = gs.quickTo(preview, "y", { duration: .6, ease: "power3" });
    window.addEventListener("pointermove", (e) => { xTo(e.clientX); yTo(e.clientY); }, { passive: true });
    rows.forEach((row, i) => {
      row.addEventListener("pointerenter", () => {
        imgs.forEach((im, j) => im.classList.toggle("is-on", i === j));
        gs.to(preview, { opacity: 1, scale: 1, rotate: (i - 1) * 4, duration: .5, ease: "expo.out", overwrite: "auto" });
      });
      row.addEventListener("pointerleave", () => gs.to(preview, { opacity: 0, scale: .85, duration: .4, ease: "power2.out", overwrite: "auto" }));
    });
  }
  rows.forEach((row) => {
    gs.from(row, { yPercent: 40, opacity: 0, duration: 1, ease: "expo.out", scrollTrigger: { trigger: row, start: "top 92%", once: true } });
  });

  /* --- manifesto: outline letters fill while scrolling --- */
  const mani = document.querySelector("[data-mani]");
  if (mani) {
    const chars = [];
    mani.querySelectorAll("[data-chars]").forEach((el) => chars.push(...splitChars(el)));
    ST.create({
      trigger: mani, start: "top 70%", end: "bottom 60%", scrub: true,
      onUpdate: (self) => {
        const lit = Math.round(self.progress * chars.length);
        chars.forEach((c, i) => c.classList.toggle("is-on", i < lit));
      }
    });
  }

  /* --- footer wordmark --- */
  const fw = document.querySelector("[data-footer-word]");
  if (fw) gs.from(fw.children, { yPercent: 100, duration: 1.2, stagger: .07, ease: "expo.out", scrollTrigger: { trigger: fw, start: "top 95%", once: true } });

  /* --- custom cursor on product images --- */
  const cursor = document.getElementById("cursor");
  if (cursor && finePointer) {
    const cx = gs.quickTo(cursor, "x", { duration: .35, ease: "power3" });
    const cy = gs.quickTo(cursor, "y", { duration: .35, ease: "power3" });
    window.addEventListener("pointermove", (e) => { cx(e.clientX); cy(e.clientY); }, { passive: true });
    document.addEventListener("pointerover", (e) => {
      const t = e.target.closest("[data-cursor]");
      if (!t || t.contains(e.relatedTarget)) return;
      if (e.target.closest(".pc__add")) return;
      cursor.textContent = t.dataset.cursor;
      gs.to(cursor, { scale: 1, duration: .45, ease: "expo.out", overwrite: "auto" });
    });
    document.addEventListener("pointerout", (e) => {
      const t = e.target.closest("[data-cursor]");
      if (!t || (e.relatedTarget && t.contains(e.relatedTarget) && !e.relatedTarget.closest(".pc__add"))) return;
      gs.to(cursor, { scale: 0, duration: .3, ease: "power2.out", overwrite: "auto" });
    });
  }

  window.addEventListener("load", () => ST.refresh());
  if (document.fonts?.ready) document.fonts.ready.then(() => ST.refresh());

  /* safety net: if the browser never delivers animation frames (some webviews, prerender),
     jump every non-scrubbed animation to its end so nothing stays hidden */
  setTimeout(() => {
    if (document.visibilityState !== "visible" || gs.ticker.frame > 2) return;
    ST.getAll().forEach((s) => { if (!s.vars.scrub && !s.pin) s.kill(false, true); });
    gs.globalTimeline.getChildren(true, true, false).forEach((t) => {
      if (!(t.scrollTrigger && t.scrollTrigger.vars.scrub)) t.progress(1);
    });
    gs.set("[data-reveal], [data-curtain], [data-hero-media], .split-w > span, .index__row, [data-footer-word] > span", { clearProps: "opacity,transform,clipPath" });
    root.classList.remove("gsap-on", "is-arriving");
    document.querySelectorAll(".mani__text .ch").forEach((c) => c.classList.add("is-on"));
  }, 3000);
}

/* ======================================================================
   Collection filters: dual price slider, instant apply, mobile sheet
   ====================================================================== */
function initFacets() {
  const form = document.querySelector("[data-facets]");
  if (!form) return;
  const isSheet = () => window.matchMedia("(max-width: 900px)").matches;

  const apply = () => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => { if (String(value).trim() !== "") params.append(key, value); });
    const range = form.querySelector("[data-price-range]");
    if (range) {
      const max = parseInt(range.dataset.max, 10);
      const minInput = range.querySelector("[data-price-min]");
      const maxInput = range.querySelector("[data-price-max]");
      if (minInput && parseInt(minInput.value, 10) <= 0) params.delete(minInput.name);
      if (maxInput && parseInt(maxInput.value, 10) >= max) params.delete(maxInput.name);
    }
    const qs = params.toString();
    window.location.href = form.action + (qs ? "?" + qs : "");
  };

  /* desktop applies instantly; on the mobile sheet the "Ergebnisse anzeigen" button applies */
  let timer;
  const autoApply = () => { if (isSheet()) return; clearTimeout(timer); timer = setTimeout(apply, 350); };
  form.addEventListener("submit", (e) => { e.preventDefault(); apply(); });
  form.querySelectorAll('input[type="checkbox"]').forEach((cb) => cb.addEventListener("change", autoApply));
  document.querySelector("[data-sort]")?.addEventListener("change", apply);

  /* dual-handle price slider kept in sync with the number fields */
  form.querySelectorAll("[data-price-range]").forEach((range) => {
    const max = parseInt(range.dataset.max, 10) || 1;
    const rMin = range.querySelector("[data-range-min]");
    const rMax = range.querySelector("[data-range-max]");
    const nMin = range.querySelector("[data-price-min]");
    const nMax = range.querySelector("[data-price-max]");
    const fill = range.querySelector("[data-price-fill]");
    const paint = () => {
      const a = parseInt(rMin.value, 10), b = parseInt(rMax.value, 10);
      fill.style.left = (a / max * 100) + "%";
      fill.style.right = (100 - b / max * 100) + "%";
    };
    const fromSlider = (e) => {
      let a = parseInt(rMin.value, 10), b = parseInt(rMax.value, 10);
      if (a > b) { if (e.target === rMin) a = b; else b = a; rMin.value = a; rMax.value = b; }
      nMin.value = a > 0 ? a : "";
      nMax.value = b < max ? b : "";
      paint();
    };
    const fromField = () => {
      const a = Math.max(0, Math.min(max, parseInt(nMin.value, 10) || 0));
      const b = Math.max(a, Math.min(max, parseInt(nMax.value, 10) || max));
      rMin.value = a; rMax.value = b; paint();
    };
    [rMin, rMax].forEach((r) => { r.addEventListener("input", fromSlider); r.addEventListener("change", autoApply); });
    [nMin, nMax].forEach((n) => { n.addEventListener("input", fromField); n.addEventListener("change", autoApply); });
    paint();
  });

  /* mobile sheet */
  const open = () => { document.body.classList.add("facets-open", "is-locked"); };
  const close = () => { document.body.classList.remove("facets-open"); if (!document.querySelector(".cd.open")) document.body.classList.remove("is-locked"); };
  document.querySelectorAll("[data-facets-open]").forEach((b) => b.addEventListener("click", open));
  document.querySelectorAll("[data-facets-close]").forEach((b) => b.addEventListener("click", close));
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
}

/* ======================================================================
   Lookbook carousel: pause while a finger rests on it
   ====================================================================== */
function initLookMarquee() {
  document.querySelectorAll("[data-marquee]").forEach((m) => {
    let timer;
    m.addEventListener("touchstart", () => { clearTimeout(timer); m.classList.add("is-paused"); }, { passive: true });
    m.addEventListener("touchend", () => { timer = setTimeout(() => m.classList.remove("is-paused"), 1800); }, { passive: true });
  });
}

/* ======================================================================
   Touch devices: the element in the middle of the screen gets the "hover" look
   ====================================================================== */
function initTouchFocus() {
  if (finePointer || !("IntersectionObserver" in window)) return;
  const targets = document.querySelectorAll("[data-index] .index__row, .grid-new .pc, #product-grid .pc, .related .pc");
  if (!targets.length) return;
  const obs = new IntersectionObserver((entries) => {
    entries.forEach((e) => e.target.classList.toggle("is-active", e.isIntersecting));
  }, { rootMargin: "-40% 0px -40% 0px" });
  targets.forEach((el) => obs.observe(el));
}

/* ======================================================================
   Page transitions (bordeaux curtain)
   ====================================================================== */
function initTransitions() {
  const curtain = document.getElementById("curtain");
  if (!curtain || reduceMotion) { root.classList.remove("is-arriving"); return; }

  if (root.classList.contains("is-arriving")) {
    try { sessionStorage.removeItem("liora-nav"); } catch (e) { /* ignore */ }
    if (window.gsap) {
      gsap.fromTo(curtain, { scaleY: 1, transformOrigin: "top" }, { scaleY: 0, duration: .7, ease: "expo.inOut", delay: .05, onComplete: () => { root.classList.remove("is-arriving"); gsap.set(curtain, { clearProps: "all" }); } });
    } else {
      root.classList.remove("is-arriving");
    }
  }

  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest("a[href]");
    if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.hash) return;
    if (/^\/(checkout|cart\/|account)/.test(url.pathname) || a.getAttribute("href").startsWith("#")) return;
    e.preventDefault();
    try { sessionStorage.setItem("liora-nav", "1"); } catch (err) { /* ignore */ }
    curtain.classList.add("is-in");
    setTimeout(() => { window.location.href = url.href; }, 460);
  });
  window.addEventListener("pageshow", (e) => { if (e.persisted) curtain.classList.remove("is-in"); });
}

document.addEventListener("DOMContentLoaded", () => {
  initHeader();
  initCart();
  initGridToggle();
  initFacets();
  initProduct();
  initMotion();
  initLookMarquee();
  initTouchFocus();
  initTransitions();
});
